/**
 * ADForm Types
 * Type definitions for the Detailed Assessment single-page form
 * Based on ATC-20-1 Detailed Evaluation Safety Assessment Form
 * Extends ARForm types with detailed hazard arrays, address, and actions fields
 */

import type {
  ARFormData,
  HazardSeverity,
  ConstructionType,
  OccupancyType,
} from '@/pages/AssessmentRapid/ARForm/ARForm.types';

// Re-export commonly used types from ARForm
export type {
  PageMode,
  HazardSeverity,
  PostingStatus,
} from '@/pages/AssessmentRapid/ARForm/ARForm.types';
export {
  HAZARD_SEVERITY_OPTIONS,
  POSTING_STATUS_OPTIONS,
  AREAS_INSPECTED_OPTIONS,
  CONSTRUCTION_TYPE_OPTIONS,
  OCCUPANCY_TYPE_OPTIONS,
  DETAILED_EVAL_TYPE_OPTIONS,
  HAZARD_LABELS,
} from '@/pages/AssessmentRapid/ARForm/ARForm.types';

/**
 * Detailed assessment damage percentage (7-level scale matching PWA estimated_building_damage)
 */
export type DetailedDamagePercentage =
  | 'none'
  | '0_1'
  | '1_10'
  | '10_30'
  | '30_60'
  | '60_100'
  | '100';

export const DAMAGE_PERCENTAGE_OPTIONS: Array<{
  value: DetailedDamagePercentage;
  label: string;
}> = [
  { value: 'none', label: 'None' },
  { value: '0_1', label: '0-1%' },
  { value: '1_10', label: '1-10%' },
  { value: '10_30', label: '10-30%' },
  { value: '30_60', label: '30-60%' },
  { value: '60_100', label: '60-100%' },
  { value: '100', label: '100%' },
];

// ============================================================================
// DETAILED HAZARD TYPES (ATC-20-1)
// ============================================================================

/** Structural hazard types (7 items) - ATC-20-1 */
export type DetailedStructuralHazardType =
  | 'FOUNDATIONS'
  | 'ROOFS_FLOORS_VERTICAL'
  | 'COLUMNS_PILASTERS_CORBELS'
  | 'DIAPHRAGMS_HORIZONTAL_BRACING'
  | 'WALLS_VERTICAL_BRACING'
  | 'PRECAST_CONNECTIONS'
  | 'OTHER';

/** Non-structural hazard types (8 items) - ATC-20-1 */
export type DetailedNonStructuralHazardType =
  | 'PARAPETS_ORNAMENTATION'
  | 'CLADDING_GLAZING'
  | 'CEILINGS_LIGHT_FIXTURES'
  | 'INTERIOR_WALLS_PARTITIONS'
  | 'ELEVATORS'
  | 'STAIRS_EXITS'
  | 'ELECTRIC_GAS'
  | 'OTHER';

/** Geotechnical hazard types (3 items) - ATC-20-1 */
export type DetailedGeotechnicalHazardType =
  | 'SLOPE_FAILURE_DEBRIS'
  | 'GROUND_MOVEMENT_FISSURES'
  | 'OTHER';

/** Hazard severity record for detailed assessment arrays */
export interface HazardSeverityRecord {
  hazardType: string;
  severity?: HazardSeverity;
  comments?: string;
  /** Description for OTHER hazard type (what the "other" hazard is) */
  otherDescription?: string;
}

// ============================================================================
// ADFORM DATA INTERFACE
// ============================================================================

export interface ADFormData extends Omit<
  ARFormData,
  | 'typeOfConstruction' | 'primaryOccupancy' | 'hazards' | 'estimatedBuildingDamage' | 'postingStatus'
  | 'storiesAboveGround' | 'storiesBelowGround' | 'buildingFootprint' | 'inhabitableResidentialUnits'
> {
  // Assessment mode override
  assessment_mode: 'detailed';

  // Overridden fields (optional in detailed form so user is forced to pick)
  typeOfConstruction?: ConstructionType;
  primaryOccupancy?: OccupancyType;
  estimatedBuildingDamage?: DetailedDamagePercentage;
  hazards: {
    collapseOrOffFoundation?: HazardSeverity;
    buildingOrStoryLeaning?: HazardSeverity;
    rackingDamageWalls?: HazardSeverity;
    chimneyParapetFallingHazard?: HazardSeverity;
    groundSlopeMovement?: HazardSeverity;
    otherDescription?: string;
    otherSeverity?: HazardSeverity;
  };

  // Extended Building Fields (Address section in ADForm)
  buildingName?: string;
  contactPhone?: string;

  // Extended Building Description
  storiesAboveGround?: number;
  storiesBelowGround?: number;
  totalResidentialUnits?: number;
  inhabitableResidentialUnits?: number;
  buildingFootprint?: number;
  numberOfUnits?: number;

  // Overall Hazard Comments (AD-specific, stored as individual API fields)
  overallHazardCollapseComments?: string;
  overallHazardOtherComments?: string;

  // General Comments (above Estimated Building Damage)
  generalComments?: string;

  // Detailed Hazard Arrays (ATC-20-1)
  structuralHazards: HazardSeverityRecord[];
  nonStructuralHazards: HazardSeverityRecord[];
  geotechnicalHazards: HazardSeverityRecord[];

  // Posting status override (AD form starts unselected, unlike ARForm)
  postingStatus?: import('@/pages/AssessmentRapid/ARForm/ARForm.types').PostingStatus;

  // Previous Posting (AD-specific)
  previousPostingInspected?: boolean;
  previousPostingRestricted?: boolean;
  previousPostingUnsafe?: boolean;
  previousInspectorId?: string;
  previousPostingDate?: string;

  // Actions Section Fields
  barricadesNeeded: boolean;
  barricadeLocations?: string;
  engineeringEvalRecommended: boolean;
  engineeringEvalStructural: boolean;
  engineeringEvalGeotechnical: boolean;
  engineeringEvalOther?: string;
  otherRecommendations?: string[];
  otherRecommendationsSpecify?: string;
  actionsComments?: string;
}

// ============================================================================
// Hazard Type Labels & Option Arrays
// ============================================================================

export const STRUCTURAL_HAZARD_TYPES: DetailedStructuralHazardType[] = [
  'FOUNDATIONS',
  'ROOFS_FLOORS_VERTICAL',
  'COLUMNS_PILASTERS_CORBELS',
  'DIAPHRAGMS_HORIZONTAL_BRACING',
  'WALLS_VERTICAL_BRACING',
  'PRECAST_CONNECTIONS',
  'OTHER',
];

export const STRUCTURAL_HAZARD_LABELS: Record<DetailedStructuralHazardType, string> = {
  FOUNDATIONS: 'Foundations',
  ROOFS_FLOORS_VERTICAL: 'Roofs/Floors (vertical load path)',
  COLUMNS_PILASTERS_CORBELS: 'Columns/Pilasters/Corbels',
  DIAPHRAGMS_HORIZONTAL_BRACING: 'Diaphragms/Horizontal bracing',
  WALLS_VERTICAL_BRACING: 'Walls/Vertical bracing',
  PRECAST_CONNECTIONS: 'Precast connections',
  OTHER: 'Other',
};

export const NON_STRUCTURAL_HAZARD_TYPES: DetailedNonStructuralHazardType[] = [
  'PARAPETS_ORNAMENTATION',
  'CLADDING_GLAZING',
  'CEILINGS_LIGHT_FIXTURES',
  'INTERIOR_WALLS_PARTITIONS',
  'ELEVATORS',
  'STAIRS_EXITS',
  'ELECTRIC_GAS',
  'OTHER',
];

export const NON_STRUCTURAL_HAZARD_LABELS: Record<DetailedNonStructuralHazardType, string> = {
  PARAPETS_ORNAMENTATION: 'Parapets/Ornamentation',
  CLADDING_GLAZING: 'Cladding/Glazing',
  CEILINGS_LIGHT_FIXTURES: 'Ceilings/Light fixtures',
  INTERIOR_WALLS_PARTITIONS: 'Interior walls/Partitions',
  ELEVATORS: 'Elevators',
  STAIRS_EXITS: 'Stairs/Exits',
  ELECTRIC_GAS: 'Electric/Gas',
  OTHER: 'Other',
};

export const GEOTECHNICAL_HAZARD_TYPES: DetailedGeotechnicalHazardType[] = [
  'SLOPE_FAILURE_DEBRIS',
  'GROUND_MOVEMENT_FISSURES',
  'OTHER',
];

export const GEOTECHNICAL_HAZARD_LABELS: Record<DetailedGeotechnicalHazardType, string> = {
  SLOPE_FAILURE_DEBRIS: 'Slope failure/Debris',
  GROUND_MOVEMENT_FISSURES: 'Ground movement/Fissures',
  OTHER: 'Other',
};

// ============================================================================
// AD-Specific Hazard Severity Options (3 options, no "None")
// ============================================================================

/** AD form uses 3 severity options: Minor/None, Moderate, Severe */
export const AD_HAZARD_SEVERITY_OPTIONS: Array<{
  value: HazardSeverity;
  label: string;
}> = [
  { value: 'minor', label: 'Minor/None' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
];

/** AD-specific overall hazard labels (only Collapse + Other remain) */
export const AD_HAZARD_LABELS: Record<string, string> = {
  collapseOrOffFoundation: 'Collapse or partial collapse',
};

// ============================================================================
// AD-Specific Option Arrays (labels differ from ARForm)
// ============================================================================

/** AD-specific construction type options (Tilt-up → "Tilt-up concrete") */
export const AD_CONSTRUCTION_TYPE_OPTIONS: Array<{
  value: import('@/pages/AssessmentRapid/ARForm/ARForm.types').ConstructionType;
  label: string;
}> = [
  { value: 'wood_frame', label: 'Wood Frame' },
  { value: 'steel_frame', label: 'Steel Frame' },
  { value: 'concrete_frame', label: 'Concrete Frame' },
  { value: 'concrete_shear_wall', label: 'Concrete Shear Wall' },
  { value: 'unreinforced_masonry', label: 'Unreinforced Masonry' },
  { value: 'reinforced_masonry', label: 'Reinforced Masonry' },
  { value: 'tilt_up', label: 'Tilt-up concrete' },
  { value: 'other', label: 'Other' },
];

/** AD-specific occupancy type options (11 options with PWA labels) */
export const AD_OCCUPANCY_TYPE_OPTIONS: Array<{
  value: import('@/pages/AssessmentRapid/ARForm/ARForm.types').OccupancyType;
  label: string;
}> = [
  { value: 'residential', label: 'Dwelling' },
  { value: 'other_residential', label: 'Other residential' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'offices', label: 'Offices' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'government', label: 'Government' },
  { value: 'school', label: 'School' },
  { value: 'hospital', label: 'Emergency services' },
  { value: 'historic', label: 'Historic' },
  { value: 'public_assembly', label: 'Public Assembly' },
  { value: 'other', label: 'Other' },
];

export const OTHER_RECOMMENDATION_OPTIONS: Array<{
  value: string;
  label: string;
}> = [
  { value: 'STRUCTURAL', label: 'Structural' },
  { value: 'GEOTECHNICAL', label: 'Geotechnical' },
  { value: 'OTHER', label: 'Other' },
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create default hazard severity records for a given set of hazard types.
 * All hazard types default to undefined severity (unselected).
 */
function createDefaultHazards<T extends string>(types: T[]): HazardSeverityRecord[] {
  return types.map((hazardType) => ({
    hazardType,
    severity: undefined,
  }));
}

/**
 * Get default form data for a new detailed assessment
 * Extends ARForm defaults with detailed-specific fields
 */
export function getDefaultDetailedFormData(
  parcelId = '',
  parcelData?: {
    address1?: string;
    address2?: string;
    city?: string;
    county?: string;
    state?: string;
    zipcode?: string;
  }
): ADFormData {
  return {
    // Assessment mode
    assessment_mode: 'detailed',

    // Metadata
    parcelId,

    // Section 1: Inspection
    inspectorId: '',
    affiliation: '',
    inspectionDateTime: new Date().toISOString(),
    areasInspected: 'exterior_only',

    // Section 2: Address / Building location
    address1: parcelData?.address1 ?? '',
    address2: parcelData?.address2,
    city: parcelData?.city ?? '',
    county: parcelData?.county ?? '',
    state: parcelData?.state ?? 'SC',
    zipcode: parcelData?.zipcode ?? '',
    buildingName: '',
    contactPhone: '',

    // Section 3: Building description
    typeOfConstruction: undefined,
    constructionTypeOther: '',
    primaryOccupancy: undefined,
    occupancyTypeOther: '',
    storiesAboveGround: undefined,
    storiesBelowGround: undefined,
    buildingFootprint: undefined,
    numberOfUnits: undefined,
    totalResidentialUnits: undefined,
    inhabitableResidentialUnits: undefined,

    // Section 4: Evaluation
    hazards: {
      collapseOrOffFoundation: undefined,
      buildingOrStoryLeaning: undefined,
      rackingDamageWalls: undefined,
      chimneyParapetFallingHazard: undefined,
      groundSlopeMovement: undefined,
      otherDescription: '',
      otherSeverity: undefined,
    },
    overallHazardCollapseComments: '',
    overallHazardOtherComments: '',
    generalComments: '',
    estimatedBuildingDamage: undefined,
    structuralHazards: createDefaultHazards(STRUCTURAL_HAZARD_TYPES),
    nonStructuralHazards: createDefaultHazards(NON_STRUCTURAL_HAZARD_TYPES),
    geotechnicalHazards: createDefaultHazards(GEOTECHNICAL_HAZARD_TYPES),

    // Section 5: Posting (AD form starts unselected)
    postingStatus: undefined,
    detailedEvalRecommended: false,
    previousPostingInspected: false,
    previousPostingRestricted: false,
    previousPostingUnsafe: false,
    previousInspectorId: '',
    previousPostingDate: '',

    // Section 6: Actions
    barricadesNeeded: false,
    barricadeLocations: '',
    engineeringEvalRecommended: false,
    engineeringEvalStructural: false,
    engineeringEvalGeotechnical: false,
    engineeringEvalOther: '',
    otherRecommendations: [],
    otherRecommendationsSpecify: '',
    actionsComments: '',

    // Section 7: Image
    photos: [],
  };
}
